import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { applyTooltips, Tooltip, DEFINITIONS } from '../utils/tooltips'
import { V, serif, mono, eyebrow, card, btnGold, btnGhost } from '../theme'
import { MiniCheckin, shouldShowCheckin } from './TradeBuilder'
import StrategyInfoTooltip from './StrategyInfoTooltip'

const MINI_EMOTIONS = [
  { key: 'calm',       emoji: '😌', label: 'Calm',        prompt: "What has created this steadiness today?" },
  { key: 'focused',    emoji: '🎯', label: 'Focused',     prompt: "What is clearest to you about this trade right now?" },
  { key: 'anxious',    emoji: '😰', label: 'Anxious',     prompt: "Where do you feel that in your body? Is this about the trade — or did it arrive with you?" },
  { key: 'rushed',     emoji: '⚡', label: 'Rushed',      prompt: "What would happen if you waited ten more minutes?" },
  { key: 'frustrated', emoji: '😤', label: 'Frustrated',  prompt: "Is this frustration about this trade — or did it come from somewhere else today?" },
  { key: 'confident',  emoji: '💪', label: 'Confident',   prompt: "Is this confidence grounded and clear — or closer to excitement?" },
  { key: 'numb',       emoji: '🌫️', label: 'Numb',        prompt: "What might you be protecting yourself from right now?" },
  { key: 'excited',    emoji: '🤑', label: 'Excited',     prompt: "Is there a plan supporting this excitement — or is the excitement the plan?" },
];

function PLTracker({ state, onUpdate, onBack, onReflect, onNavigate }) {
  const [trades, setTrades] = useState(state.plTrades || []);
  const [form, setForm] = useState(() => ({ ticker: '', strategy: 'CSP', entryDate: new Date().toISOString().split('T')[0], expiryDate: '', strike: '', strike2: '', credit: '', qty: '1', status: 'Open', pnl: '', notes: '' }));
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [plTab, setPlTab] = useState('trades');
  // Pre-trade check-in state
  const [showPreCheckin, setShowPreCheckin] = useState(false);
  const [pendingForm, setPendingForm] = useState(null);
  // Post-trade prompt state
  const [showPostPrompt, setShowPostPrompt] = useState(false);
  const [postTradeData, setPostTradeData] = useState(null);
  const [bypassWarning, setBypassWarning] = useState(false);
  const [currentMarkInputs, setCurrentMarkInputs] = useState({});
  const [tradeFilter, setTradeFilter] = useState('all');
  const [sortOrder,   setSortOrder]   = useState('newest');
  const [ccShares,    setCcShares]    = useState('');
  const [ccCostBasis, setCcCostBasis] = useState('');
  const [patternsNote, setPatternsNote] = useState(() => {
    try { return localStorage.getItem('tlaw_patterns_note') || ''; } catch { return ''; }
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ── Risk helpers ─────────────────────────────────────────────
  const accountSize = state.accountSize || 0;
  const isSpreadStrategy = (strategy) => ['Bull Put Spread', 'Bear Call Spread', '0DTE Spread'].includes(strategy);
  const calcMaxLoss = (strategy, shortStrike, longStrike, premium, contracts) => {
    const s = parseFloat(shortStrike) || 0;
    const l = parseFloat(longStrike) || 0;
    const p = parseFloat(premium) || 0;
    const c = parseInt(contracts) || 1;
    switch (strategy) {
      case 'CSP':
      case 'Wheel':
        return (s - p) * 100 * c;
      case 'CC':
        return 0;
      case 'Bull Put Spread':
        return Math.max(0, (s - l - p) * 100 * c);
      case 'Bear Call Spread':
        return Math.max(0, (l - s - p) * 100 * c);
      case 'LEAPS':
        return p * 100 * c;
      default:
        return (s - p) * 100 * c;
    }
  };
  const tradeMaxLoss = (t) => calcMaxLoss(t.strategy, t.strike, t.strike2, t.credit, t.qty);
  const tradeTheoreticalLoss = (t) => {
    const credit = parseFloat(t.credit) || 0;
    const qty = parseInt(t.qty) || 1;
    if (isSpreadStrategy(t.strategy)) {
      const s1 = parseFloat(t.strike) || 0;
      const s2 = parseFloat(t.strike2) || 0;
      if (s1 > 0 && s2 > 0) return Math.abs(s1 - s2) * qty * 100;
    }
    return credit * qty * 100 * 2;
  };
  const riskColor = (pct) => pct < 1 ? V.sage : pct < 2 ? V.gold : V.rose;
  const getRiskDisplay = (strategy, maxLoss, accountBalance) => {
    const pct = accountBalance > 0 ? (maxLoss / accountBalance * 100) : 0;
    if (strategy === 'Bull Put Spread' || strategy === 'Bear Call Spread' || strategy === '0DTE Spread') {
      return { label: 'Max Loss Risk', pct: pct.toFixed(1), guideline: '1-2%', isWithinGuideline: pct <= 2, explanation: 'Defined risk strategy — this is your worst-case loss if the trade goes completely against you.' };
    }
    if (strategy === 'CSP' || strategy === 'Wheel') {
      return { label: 'Capital Concentration', pct: pct.toFixed(1), guideline: '5-10%', isWithinGuideline: pct <= 10, explanation: 'Assignment strategy — this measures how much of your account is tied up in this one stock if assigned. Not a loss measure.' };
    }
    if (strategy === 'CC') {
      return { label: 'Additional Capital Risk', pct: '0.0', guideline: 'N/A', isWithinGuideline: true, explanation: 'You already own these shares. This call generates income with no additional capital at risk.' };
    }
    return { label: 'Risk', pct: pct.toFixed(1), guideline: '1-2%', isWithinGuideline: pct <= 2, explanation: '' };
  };
  const openTrades = trades.filter(t => t.status === 'Open');
  const currentOpenMaxLoss = openTrades.reduce((sum, t) => sum + tradeMaxLoss(t), 0);
  const currentOpenRisk = accountSize > 0 ? (currentOpenMaxLoss / accountSize) * 100 : 0;
  const formCredit = parseFloat(form.credit) || 0;
  const formQty = parseInt(form.qty) || 1;
  const formStrike = parseFloat(form.strike) || 0;
  const formStrike2 = parseFloat(form.strike2) || 0;
  const formIsSpread = isSpreadStrategy(form.strategy);
  const formSpreadWidth = formIsSpread && formStrike > 0 && formStrike2 > 0 ? Math.abs(formStrike - formStrike2) : 0;
  const formMaxLoss = calcMaxLoss(form.strategy, form.strike, form.strike2, form.credit, form.qty);
  const formRiskPct = accountSize > 0 ? (formMaxLoss / accountSize) * 100 : 0;
  const afterRisk = currentOpenRisk + formRiskPct;
  const formMaxLossPerContract = calcMaxLoss(form.strategy, form.strike, form.strike2, form.credit, 1);
  const maxCtrAt2 = accountSize > 0 && formMaxLossPerContract > 0 ? Math.floor((accountSize * 0.02) / formMaxLossPerContract) : 0;
  const reducedRiskPct = accountSize > 0 && maxCtrAt2 > 0 ? (formMaxLossPerContract * maxCtrAt2 / accountSize) * 100 : 0;
  const formSpreadWide = formIsSpread && formSpreadWidth > 50;
  const showRiskWarning = !bypassWarning && form.strategy !== 'CC' && accountSize > 0 && formCredit > 0 && (formRiskPct > 2 || afterRisk > 5 || formSpreadWide);

  // ── Inner work prompt visibility ─────────────────────────────
  const shouldShowPrompt = () => {
    const s = state.innerWorkPrompts || 'always';
    if (s === 'never') return false;
    if (s === 'once_daily') {
      try { return localStorage.getItem('tlaw_lastPromptDate') !== new Date().toDateString(); } catch { return true; }
    }
    return true;
  };

  const commitTrade = (tradeForm, emotion = null) => {
    const newTrade = { ...tradeForm, id: Date.now(), date: new Date().toLocaleDateString(), emotion };
    const newTrades = [...trades, newTrade];
    setTrades(newTrades);
    onUpdate && onUpdate({ plTrades: newTrades });
    if (emotion && onUpdate) {
      const entry = { timestamp: Date.now(), date: new Date().toLocaleDateString(), type: 'pre_trade', emotion, tradeDetails: { ticker: tradeForm.ticker, strategy: tradeForm.strategy } };
      onUpdate({ innerWorkCheckins: [...(state.innerWorkCheckins || []), entry] });
    }
    setForm({ ticker: '', strategy: 'CSP', entryDate: new Date().toISOString().split('T')[0], expiryDate: '', strike: '', strike2: '', credit: '', qty: '1', status: 'Open', pnl: '', notes: '' });
    setPendingForm(null);
    setShowPreCheckin(false);
    setBypassWarning(false);
    setPostTradeData(newTrade);
    if (shouldShowPrompt()) {
      setShowPostPrompt(true);
      try { localStorage.setItem('tlaw_lastPromptDate', new Date().toDateString()); } catch {}
    }
  };

  const addTrade = () => {
    if (!form.ticker || !form.strike || !form.credit) return;
    if (shouldShowCheckin(state)) {
      setPendingForm({ ...form });
      setShowPreCheckin(true);
    } else {
      commitTrade(form);
    }
  };

  const startEdit = (t) => { setEditId(t.id); setEditForm({ ...t }); setDeleteConfirm(null); setShowPostPrompt(false); };
  const cancelEdit = () => { setEditId(null); setEditForm({}); };
  const saveTrade = () => {
    const newTrades = trades.map(t => t.id === editId ? { ...editForm } : t);
    setTrades(newTrades);
    onUpdate && onUpdate({ plTrades: newTrades });
    setEditId(null);
    setPostTradeData({ ...editForm });
    if (shouldShowPrompt()) {
      setShowPostPrompt(true);
      try { localStorage.setItem('tlaw_lastPromptDate', new Date().toDateString()); } catch {}
    }
    setEditForm({});
  };
  const deleteTrade = (id) => {
    const newTrades = trades.filter(t => t.id !== id);
    setTrades(newTrades);
    onUpdate && onUpdate({ plTrades: newTrades });
    setDeleteConfirm(null);
  };

  const updateCurrentMark = (tradeId, raw) => {
    const val = parseFloat(raw);
    const newTrades = trades.map(t => t.id === tradeId ? { ...t, currentMark: raw === '' ? null : isNaN(val) ? null : val } : t);
    setTrades(newTrades);
    onUpdate && onUpdate({ plTrades: newTrades });
  };

  const totalPnL = trades.reduce((sum, t) => sum + (parseFloat(t.pnl) || 0), 0);
  const closedTrades = trades.filter(t => t.status !== 'Open');
  const winRate = closedTrades.length > 0 ? Math.round((closedTrades.filter(t => parseFloat(t.pnl) > 0).length / closedTrades.length) * 100) : 0;
  const checkins = state.innerWorkCheckins || [];
  const totalTaxEstimate = closedTrades.reduce((sum, t) => { const p = parseFloat(t.pnl); return sum + (p > 0 ? p * 0.28 : 0); }, 0);

  const inp = { padding: '10px 12px', background: V.bg3, border: `0.5px solid ${V.border}`, borderRadius: '8px', color: V.text, fontSize: '13px', outline: 'none', width: '100%', maxWidth: '100%', boxSizing: 'border-box', minHeight: '44px', fontFamily: "'Jost', sans-serif", display: 'block' };
  const inpSm = { ...inp, padding: '5px 7px', fontSize: '12px', minHeight: 'unset' };
  const actBtn = (color) => ({ padding: '4px 9px', background: 'transparent', border: `0.5px solid ${color}`, borderRadius: '5px', color, fontSize: '11px', cursor: 'pointer' });
  const fLabel = { fontSize: '11px', fontFamily: "'Jost', sans-serif", color: V.muted, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '3px', fontWeight: 500 };
  const fNote = { fontSize: '11px', fontFamily: "'Jost', sans-serif", color: V.dim, marginBottom: '6px' };
  const col2 = { gridColumn: '1 / -1' };

  return (
    <div style={{ padding: '28px 24px', width: '100%', maxWidth: '100%', overflowX: 'hidden', boxSizing: 'border-box' }}>
      <button style={{ ...btnGhost, marginBottom: '20px', fontSize: '12px' }} onClick={onBack}>← Dashboard</button>
      <div style={{ ...eyebrow, marginBottom: '6px' }}>Practice Tool</div>
      <h2 style={{ ...serif, fontSize: '28px', fontWeight: 500, margin: '0 0 4px' }}>Profit & Loss</h2>
      <p style={{ fontSize: '13px', color: V.muted, marginBottom: '16px' }}>Log your trades. Track your patterns.</p>

      <div style={{ background: V.bg2, borderLeft: `3px solid ${V.gold}`, borderRadius: '10px', padding: '16px 20px', marginBottom: '24px', display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
        <span style={{ fontSize: '20px', color: V.gold, lineHeight: 1, flexShrink: 0 }}>📊</span>
        <p style={{ ...serif, fontSize: '15px', color: V.muted, fontStyle: 'italic', lineHeight: 1.8, margin: 0 }}>
          Your brokerage shows you what happened. This platform shows you why it happened. Every trade you log here builds a picture of yourself as a trader that no brokerage statement ever could.
        </p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', marginBottom: '24px', borderBottom: `0.5px solid ${V.border}` }}>
        {[{ id: 'trades', label: 'My Trades' }, { id: 'patterns', label: 'My Patterns' }].map(({ id, label }) => (
          <button key={id} onClick={() => setPlTab(id)} style={{ padding: '10px 24px', background: 'transparent', border: 'none', borderBottom: plTab === id ? `2px solid ${V.gold}` : '2px solid transparent', color: plTab === id ? V.gold : V.muted, fontSize: '13px', cursor: 'pointer', fontFamily: "'Jost', sans-serif" }}>
            {label}
          </button>
        ))}
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '20px' }}>
        {[
          { label: 'TOTAL TRADES', val: trades.length, color: V.gold },
          { label: 'OPEN',         val: trades.filter(t => t.status === 'Open').length, color: V.sky },
          { label: 'WIN RATE',     val: closedTrades.length ? `${winRate}%` : '—', color: V.sage },
          { label: 'TOTAL P&L',    val: `$${totalPnL.toFixed(0)}`, color: totalPnL >= 0 ? V.sage : V.rose },
        ].map(({ label, val, color }) => (
          <div key={label} style={{ background: V.bg2, border: `0.5px solid ${V.border}`, borderRadius: '10px', padding: '14px', flex: '1 1 120px', minWidth: 0 }}>
            <div style={{ ...mono, fontSize: '22px', fontWeight: 500, color, marginBottom: '4px' }}>{val}</div>
            <div style={{ fontSize: '10px', color: V.muted, letterSpacing: '0.07em' }}>{label}</div>
          </div>
        ))}
      </div>

      {plTab === 'trades' && (
      <React.Fragment>
      {/* Add trade form */}
      <div style={{ background: V.bg2, border: `0.5px solid ${V.border}`, borderRadius: '10px', padding: '18px 20px', marginBottom: '14px', width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
        <div style={{ ...eyebrow, marginBottom: '16px' }}>Log a Trade</div>
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', maxWidth: '100%', gap: '16px', marginBottom: '16px' }}>
          {/* Underlying Symbol */}
          <div style={{ width: '100%', minWidth: 0 }}>
            <div style={fLabel}>Underlying Symbol</div>
            <div style={fNote}>Ticker symbol, e.g. AAPL, SPY, TSLA</div>
            <input style={inp} placeholder="AAPL" value={form.ticker} onChange={e => setForm(f => ({ ...f, ticker: e.target.value.toUpperCase() }))} />
          </div>
          {/* Strategy */}
          <div style={{ width: '100%', minWidth: 0 }}>
            <div style={fLabel}>Strategy<StrategyInfoTooltip /></div>
            <div style={fNote}>The type of trade you placed</div>
            <select style={inp} value={form.strategy} onChange={e => {
              const s = e.target.value;
              setForm(f => ({ ...f, strategy: s }));
            }}>
              {['CSP', 'CC', 'Wheel', 'Bull Put Spread', 'Bear Call Spread', 'LEAPS'].map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          {/* CC-specific fields */}
          {form.strategy === 'CC' && (
            <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '16px' }}>
              <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                <div style={fLabel}>Shares Owned</div>
                <div style={fNote}>Number of shares this call is written against. Must be 100 per contract.</div>
                <input style={inp} type="number" min="100" step="100" placeholder="e.g. 100" value={ccShares} onChange={e => setCcShares(e.target.value)} />
              </div>
              <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                <div style={fLabel}>Cost Basis Per Share ($)</div>
                <div style={fNote}>Your effective cost basis on these shares after all previous premiums collected. Used to verify strike is above your cost basis.</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontFamily: "'Jost',sans-serif", fontSize: '14px', color: 'rgba(240,237,230,0.6)' }}>$</span>
                  <input style={{ ...inp, flex: 1 }} type="number" min="0" step="0.01" placeholder="e.g. 58.50" value={ccCostBasis} onChange={e => setCcCostBasis(e.target.value)} />
                </div>
              </div>
            </div>
          )}
          {/* CC cost basis validation */}
          {form.strategy === 'CC' && parseFloat(form.strike) > 0 && parseFloat(ccCostBasis) > 0 && (() => {
            const strike = parseFloat(form.strike);
            const costBasis = parseFloat(ccCostBasis);
            const shares = parseFloat(ccShares) || 100;
            const contracts = parseInt(form.qty) || 1;
            const premium = parseFloat(form.credit) || 0;
            const shareProfit = (strike - costBasis) * shares;
            const premiumIncome = premium * 100 * contracts;
            const total = shareProfit + premiumIncome;
            if (strike > costBasis) {
              return (
                <div style={{ padding: '12px 16px', background: 'rgba(122,171,138,0.08)', border: `1px solid ${V.sage}`, borderRadius: '10px' }}>
                  <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.sage, fontWeight: 600, marginBottom: '6px' }}>✓ Strike above cost basis</div>
                  <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: 'rgba(240,237,230,0.7)', lineHeight: 1.7 }}>
                    Called away at ${strike.toFixed(2)} locks in <strong style={{ color: V.sage }}>${shareProfit.toFixed(0)}</strong> profit on shares
                    {premium > 0 && <> plus <strong style={{ color: V.sage }}>${premiumIncome.toFixed(0)}</strong> premium income.</>}
                    {premium > 0 && <> Total if called: <strong style={{ color: V.sage }}>${total.toFixed(0)}</strong>.</>}
                  </div>
                </div>
              );
            } else if (strike < costBasis) {
              return (
                <div style={{ padding: '12px 16px', background: 'rgba(196,122,122,0.08)', border: `1px solid ${V.rose}`, borderRadius: '10px' }}>
                  <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.rose, fontWeight: 600, marginBottom: '6px' }}>⚠ Strike below cost basis</div>
                  <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: 'rgba(240,237,230,0.7)', lineHeight: 1.7 }}>
                    If called away at ${strike.toFixed(2)} you would realize a loss of <strong style={{ color: V.rose }}>${Math.abs(shareProfit).toFixed(0)}</strong> on the shares. Consider a higher strike.
                  </div>
                </div>
              );
            } else {
              return (
                <div style={{ padding: '12px 16px', background: 'rgba(201,168,76,0.08)', border: `1px solid ${V.gold}`, borderRadius: '10px' }}>
                  <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.gold, fontWeight: 600, marginBottom: '6px' }}>At cost basis</div>
                  <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: 'rgba(240,237,230,0.7)', lineHeight: 1.7 }}>
                    Shares break even if called away. Premium collected is your only profit on this trade.
                  </div>
                </div>
              );
            }
          })()}
          {/* Entry Date + Expiration Date — side by side, wrap on narrow */}
          <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '16px' }}>
            <div style={{ flex: '1 1 200px', minWidth: 0 }}>
              <div style={fLabel}>Entry Date</div>
              <div style={fNote}>When you opened this position</div>
              <input style={inp} type="date" value={form.entryDate} onChange={e => setForm(f => ({ ...f, entryDate: e.target.value }))} />
            </div>
            <div style={{ flex: '1 1 200px', minWidth: 0 }}>
              <div style={fLabel}>{applyTooltips('Expiration Date')}</div>
              <div style={fNote}>{applyTooltips('Contract expiry date')}</div>
              <input style={inp} type="date" value={form.expiryDate} onChange={e => setForm(f => ({ ...f, expiryDate: e.target.value }))} />
            </div>
          </div>
          {/* Short Strike + Long Strike — side by side, wrap on narrow */}
          <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '16px' }}>
            <div style={{ flex: '1 1 200px', minWidth: 0 }}>
              <div style={fLabel}>{applyTooltips('Short Strike')}</div>
              <div style={fNote}>{applyTooltips('The strike price you sold')}</div>
              <input style={inp} placeholder="e.g. 420" value={form.strike} onChange={e => setForm(f => ({ ...f, strike: e.target.value }))} />
            </div>
            <div style={{ flex: '1 1 200px', minWidth: 0 }}>
              <div style={fLabel}>{applyTooltips('Long Strike')} <span style={{ color: V.dim, fontWeight: 400, textTransform: 'none' }}>(optional)</span></div>
              <div style={fNote}>{applyTooltips('For spreads — the strike you bought')}</div>
              <input style={inp} placeholder="e.g. 415" value={form.strike2} onChange={e => setForm(f => ({ ...f, strike2: e.target.value }))} />
            </div>
          </div>
          {/* Spread width validation */}
          {formIsSpread && formStrike > 0 && formStrike2 > 0 && (
            <div style={{ padding: '10px 14px', background: V.bg3, border: `0.5px solid ${formSpreadWidth > 50 ? V.gold : V.border}`, borderRadius: '8px' }}>
              <span style={{ fontSize: '12px', fontFamily: "'Jost',sans-serif", color: V.muted }}>
                Spread width: <strong style={{ color: V.text }}>${formSpreadWidth.toFixed(0)}</strong>
              </span>
              {formSpreadWidth > 50 && (
                <p style={{ fontSize: '12px', fontFamily: "'Jost',sans-serif", color: V.gold, margin: '6px 0 0', lineHeight: 1.6 }}>
                  Wide spread detected. Verify your strikes are correct. A ${formSpreadWidth.toFixed(0)} wide spread has a max loss of ${(formSpreadWidth * 100 * formQty).toFixed(0)} before premium.
                </p>
              )}
            </div>
          )}

          {/* Premium + Contracts — side by side, wrap on narrow */}
          <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '16px' }}>
            <div style={{ flex: '1 1 200px', minWidth: 0 }}>
              <div style={fLabel}>{applyTooltips('Premium Received')}</div>
              <div style={fNote}>{applyTooltips('Credit per share, e.g. 1.50')}</div>
              <input style={inp} placeholder="0.00" value={form.credit} onChange={e => setForm(f => ({ ...f, credit: e.target.value }))} />
            </div>
            <div style={{ flex: '1 1 200px', minWidth: 0 }}>
              <div style={fLabel}>{applyTooltips('Contracts')}</div>
              <div style={fNote}>{applyTooltips('Number of contracts (1 = 100 shares)')}</div>
              <input style={inp} type="number" min="1" placeholder="1" value={form.qty} onChange={e => setForm(f => ({ ...f, qty: e.target.value }))} />
            </div>
          </div>
          {/* Outcome */}
          <div style={{ width: '100%', minWidth: 0 }}>
            <div style={fLabel}>Outcome</div>
            <div style={fNote}>Current status of this trade</div>
            <select style={inp} value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
              {['Open', 'Closed - Profit', 'Closed - Loss', 'Closed - Breakeven', 'Assigned', 'Rolled', 'Expired Worthless'].map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          {/* P&L — only when closed */}
          {form.status !== 'Open' && (
            <div style={{ width: '100%', minWidth: 0 }}>
              <div style={fLabel}>P&L</div>
              <div style={fNote}>Final profit or loss, e.g. 125 or -60</div>
              <input style={inp} placeholder="0" value={form.pnl} onChange={e => setForm(f => ({ ...f, pnl: e.target.value }))} />
            </div>
          )}
          {/* Notes */}
          <div style={{ width: '100%', minWidth: 0 }}>
            <div style={fLabel}>Notes <span style={{ color: V.dim, fontWeight: 400, textTransform: 'none' }}>(optional)</span></div>
            <div style={fNote}>Your thesis — why this strike, this expiry?</div>
            <textarea style={{ ...inp, minHeight: '72px', resize: 'vertical' }} placeholder="What was your thesis? What drew you to this trade?" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          </div>
        </div>

        {/* CC income summary */}
        {form.strategy === 'CC' && formCredit > 0 && (() => {
          const premiumIncome = formCredit * 100 * formQty;
          const strike = parseFloat(form.strike) || 0;
          const costBasis = parseFloat(ccCostBasis) || 0;
          const shares = parseFloat(ccShares) || 100;
          const profitIfCalled = costBasis > 0 && strike > 0 ? (strike - costBasis) * shares + premiumIncome : null;
          return (
            <div style={{ background: V.bg2, border: `1px solid ${V.sage}`, borderRadius: '10px', padding: '16px', marginBottom: '12px' }}>
              <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '11px', letterSpacing: '0.15em', textTransform: 'uppercase', color: V.sage, marginBottom: '14px', fontWeight: 600 }}>Covered Call Income Summary</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <span style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: 'rgba(240,237,230,0.6)' }}>Premium Income</span>
                  <span style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.sage, fontWeight: 500 }}>${premiumIncome.toFixed(0)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <span style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: 'rgba(240,237,230,0.6)' }}>Cost Basis Impact</span>
                  <span style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.sage, fontWeight: 500 }}>Reduces cost basis by ${formCredit.toFixed(2)}/share</span>
                </div>
                {strike > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <span style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: 'rgba(240,237,230,0.6)' }}>Called Away Price</span>
                    <span style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.gold, fontWeight: 500 }}>${strike.toFixed(2)} per share</span>
                  </div>
                )}
                {profitIfCalled !== null && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <span style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: 'rgba(240,237,230,0.6)' }}>Profit if Called</span>
                    <span style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: profitIfCalled >= 0 ? V.sage : V.rose, fontWeight: 500 }}>${profitIfCalled.toFixed(0)}</span>
                  </div>
                )}
              </div>
              <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '11px', color: 'rgba(240,237,230,0.45)', fontStyle: 'italic', lineHeight: 1.65, margin: '12px 0 0' }}>
                Covered calls are not included in your capital at risk calculation. This is income on shares you already own.
              </p>
            </div>
          );
        })()}

        {/* Real-time risk summary — hidden for CC */}
        {formCredit > 0 && form.strategy !== 'CC' && (
          <div style={{ background: V.bg3, border: `0.5px solid rgba(201,168,76,0.2)`, borderRadius: '8px', padding: '16px', marginBottom: '12px' }}>
            {accountSize <= 0 ? (
              <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.gold, margin: 0 }}>
                <span onClick={() => onNavigate && onNavigate('config')} style={{ cursor: 'pointer', textDecoration: 'underline' }}>
                  Set your account size in Settings to see risk calculations →
                </span>
              </p>
            ) : (
              <>
                <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '10px', letterSpacing: '0.2em', textTransform: 'uppercase', color: V.muted, marginBottom: '12px' }}>Risk Summary</div>
                {/* Row 1 — Max Loss */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted }}>Max loss on this trade</span>
                  <span style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.text, fontWeight: 500 }}>${formMaxLoss.toFixed(0)}</span>
                </div>
                {/* Row 2 — Strategy-aware risk display */}
                {(() => {
                  const rd = getRiskDisplay(form.strategy, formMaxLoss, accountSize);
                  const dotColor = rd.isWithinGuideline ? V.sage : V.rose;
                  return (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <span style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted }}>{rd.label}</span>
                        <span style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: dotColor, display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: dotColor, display: 'inline-block', flexShrink: 0 }} />
                          {rd.pct}%
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: rd.explanation ? '6px' : '8px' }}>
                        <span style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.dim }}>Target: {rd.guideline}</span>
                        <span style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: rd.isWithinGuideline ? V.sage : V.gold }}>{rd.isWithinGuideline ? '✓ Within guideline' : '⚠ Above guideline'}</span>
                      </div>
                      {rd.explanation && (
                        <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.dim, lineHeight: 1.65, margin: '0 0 8px', fontStyle: 'italic' }}>{rd.explanation}</p>
                      )}
                    </>
                  );
                })()}
                {/* Row 3 — Current open risk */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted }}>Current open risk</span>
                  <span style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: riskColor(currentOpenRisk), display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: riskColor(currentOpenRisk), display: 'inline-block', flexShrink: 0 }} />
                    {currentOpenRisk.toFixed(1)}%
                  </span>
                </div>
                {/* Row 4 — After this trade */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <span style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted }}>After this trade</span>
                  <span style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: riskColor(afterRisk), display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: riskColor(afterRisk), display: 'inline-block', flexShrink: 0 }} />
                    {afterRisk.toFixed(1)}% —{' '}
                    <em style={{ fontStyle: 'italic' }}>
                      {afterRisk < 2 ? 'Well within guidelines' : afterRisk < 5 ? 'Approaching threshold' : 'Above recommended maximum'}
                    </em>
                  </span>
                </div>
                {/* Progress bar */}
                <div style={{ position: 'relative', height: '6px', borderRadius: '4px', overflow: 'hidden', background: 'rgba(240,237,230,0.07)', marginBottom: '4px' }}>
                  <div style={{ position: 'absolute', inset: 0, display: 'flex' }}>
                    <div style={{ width: '20%', background: V.sage, opacity: 0.45 }} />
                    <div style={{ width: '30%', background: V.gold, opacity: 0.45 }} />
                    <div style={{ flex: 1, background: V.rose, opacity: 0.45 }} />
                  </div>
                  <div style={{ position: 'absolute', top: 0, bottom: 0, width: '2px', left: `${Math.min(afterRisk / 10 * 100, 99)}%`, background: '#f0ede6', borderRadius: '1px' }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: "'Jost',sans-serif", fontSize: '10px', color: V.dim }}>
                  <span>0%</span><span>2%</span><span>5%</span><span>10%+</span>
                </div>
              </>
            )}
          </div>
        )}

        {/* Risk warning */}
        {showRiskWarning && (
          <div style={{ background: 'rgba(196,122,122,0.1)', border: `1px solid ${V.rose}`, borderRadius: '8px', padding: '12px 16px', marginBottom: '12px' }}>
            <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.text, lineHeight: 1.75, margin: '0 0 10px', whiteSpace: 'pre-line' }}>
              {formSpreadWide
                ? `⚠ Spread width of $${formSpreadWidth} looks unusually wide. Standard spreads are typically $5–$25 wide.\n\nPlease verify:\nShort Strike: $${Math.max(formStrike, formStrike2)}\nLong Strike: $${Math.min(formStrike, formStrike2)}\n\nDid you mean to enter these strikes? A $${formSpreadWidth} wide spread has a max loss of $${formMaxLossPerContract.toFixed(0)} per contract.`
                : maxCtrAt2 < 1
                  ? `⚠ At this spread width one contract exceeds your 2% per trade limit. Consider a narrower spread width ($5–$25) or waiting until your account size supports this trade.`
                  : formRiskPct > 2
                    ? `⚠ This trade risks ${formRiskPct.toFixed(1)}% of your portfolio. To stay within your 2% per trade limit consider trading ${maxCtrAt2} ${maxCtrAt2 === 1 ? 'contract' : 'contracts'} instead of ${formQty}.`
                    : `⚠ Adding this trade brings your total portfolio risk to ${afterRisk.toFixed(1)}%. Suggested maximum is 5% across all open positions. Consider waiting for an existing position to close first.`}
            </p>
            <button style={{ background: 'transparent', border: 'none', color: V.dim, fontFamily: "'Jost',sans-serif", fontSize: '12px', cursor: 'pointer', padding: '4px 0' }} onClick={() => setBypassWarning(true)}>
              I understand — continue anyway
            </button>
          </div>
        )}

        {!showPreCheckin && (
          <div>
            <button style={{ ...btnGold, opacity: form.ticker && form.strike && form.credit ? 1 : 0.4, minHeight: '48px', width: '100%', fontSize: '15px' }} onClick={addTrade}>Add Trade →</button>
            <p style={{ fontSize: '11px', color: V.dim, textAlign: 'center', marginTop: '8px', marginBottom: 0, fontFamily: "'Jost', sans-serif" }}>Saved locally on this device</p>
          </div>
        )}
      </div>

      {/* Pre-trade check-in */}
      {showPreCheckin && pendingForm && (
        <MiniCheckin
          title="Before you log this trade"
          subtitle="Take a moment. How are you arriving at this position?"
          confirmLabel="I've checked in — save my trade →"
          skipLabel="Skip and save →"
          tradeDetails={{ ticker: pendingForm.ticker, strategy: pendingForm.strategy }}
          onCheckin={({ emotion, note }) => commitTrade(pendingForm, emotion || null)}
          onSkip={() => commitTrade(pendingForm)}
        />
      )}

      {/* Post-trade prompt */}
      {showPostPrompt && postTradeData && (
        <div style={{ background: V.bg2, borderLeft: `3px solid ${V.gold}`, borderRadius: '10px', padding: '24px', marginTop: '12px' }}>
          <h3 style={{ ...serif, fontSize: '17px', color: V.gold, marginBottom: '10px', fontWeight: 400 }}>Before you move on</h3>
          <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, lineHeight: 1.75, marginBottom: '16px' }}>
            The most valuable thing you can do right now — while this trade is still fresh — is spend two minutes in the Inner Work tab. Not about the numbers. About what happened inside you while this trade was unfolding.
          </p>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button style={{ ...btnGold, padding: '10px 20px', fontSize: '13px', minHeight: '44px' }} onClick={() => { setShowPostPrompt(false); onReflect && onReflect(postTradeData); }}>
              Open Inner Work →
            </button>
            <button style={{ ...btnGhost, padding: '10px 18px', fontSize: '13px', minHeight: '44px' }} onClick={() => { setShowPostPrompt(false); onNavigate && onNavigate('meditations'); }}>
              Take a moment to meditate →
            </button>
            <button style={{ ...btnGhost, padding: '10px 18px', fontSize: '13px', minHeight: '44px' }} onClick={() => setShowPostPrompt(false)}>
              Continue trading →
            </button>
          </div>
        </div>
      )}

      {/* Trade list — stacked cards */}
      {trades.length > 0 && (() => {
        // Filter counts
        const fc = {
          all:      trades.length,
          open:     trades.filter(t => t.status === 'Open').length,
          profit:   trades.filter(t => t.status === 'Closed - Profit').length,
          loss:     trades.filter(t => t.status === 'Closed - Loss').length,
          rolled:   trades.filter(t => t.status === 'Rolled').length,
          assigned: trades.filter(t => t.status === 'Assigned').length,
          expired:  trades.filter(t => t.status === 'Expired Worthless').length,
        };
        const statusMap = { open: 'Open', profit: 'Closed - Profit', loss: 'Closed - Loss', rolled: 'Rolled', assigned: 'Assigned', expired: 'Expired Worthless' };
        let filtered = tradeFilter === 'all' ? [...trades] : trades.filter(t => t.status === statusMap[tradeFilter]);
        switch (sortOrder) {
          case 'oldest':   filtered.sort((a, b) => new Date(a.entryDate || a.date) - new Date(b.entryDate || b.date)); break;
          case 'highest':  filtered.sort((a, b) => (parseFloat(b.credit) || 0) - (parseFloat(a.credit) || 0)); break;
          case 'lowest':   filtered.sort((a, b) => (parseFloat(a.credit) || 0) - (parseFloat(b.credit) || 0)); break;
          case 'strategy': filtered.sort((a, b) => (a.strategy || '').localeCompare(b.strategy || '')); break;
          case 'symbol':   filtered.sort((a, b) => (a.ticker || '').localeCompare(b.ticker || '')); break;
          default:         filtered.reverse(); break; // newest
        }
        const filterName = { all: 'trades', open: 'open positions', profit: 'profitable trades', loss: 'losing trades', rolled: 'rolled trades', assigned: 'assigned trades', expired: 'expired worthless trades' };
        const emptyMsg = {
          open:     'No open positions logged. When you enter a trade, set the outcome to Open to track it here.',
          profit:   'No profitable trades logged yet. Keep following your rules — they are coming.',
          loss:     'No losing trades logged.',
          rolled:   'No rolled trades logged.',
          assigned: 'No assigned trades logged.',
          expired:  'No expired worthless trades logged.',
        };
        const pills = [
          { key: 'all',      label: `All (${fc.all})`,            selColor: V.gold },
          { key: 'open',     label: `Open (${fc.open})`,          selColor: V.sky  },
          { key: 'profit',   label: `Profit (${fc.profit})`,      selColor: V.sage },
          { key: 'loss',     label: `Loss (${fc.loss})`,          selColor: V.rose },
          { key: 'rolled',   label: `Rolled (${fc.rolled})`,      selColor: V.gold },
          { key: 'assigned', label: `Assigned (${fc.assigned})`,  selColor: V.gold },
          { key: 'expired',  label: `Expired (${fc.expired})`,    selColor: V.sage },
        ];
        return (
        <div style={{ marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div style={{ ...eyebrow, marginBottom: '4px' }}>Trade History</div>
          <div style={{ background: V.bg2, borderLeft: `2px solid ${V.gold}`, borderRadius: '8px', padding: '12px 16px', marginBottom: '4px' }}>
            <p style={{ fontSize: '12px', color: V.muted, fontFamily: "'Jost',sans-serif", fontStyle: 'italic', lineHeight: 1.7, margin: 0 }}>
              Capital at Risk reflects premium collected at entry. As your positions decay toward expiration your actual risk decreases. Enter the current mark price on any open trade to see your real-time risk. Update the outcome when positions close.
            </p>
          </div>

          {/* Filter bar */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', marginBottom: '4px' }}>
            {pills.map(p => {
              const sel = tradeFilter === p.key;
              return (
                <button key={p.key} onClick={() => setTradeFilter(p.key)} style={{ padding: '6px 16px', borderRadius: '20px', border: `1px solid ${sel ? p.selColor : V.border}`, background: sel ? `${p.selColor}22` : 'transparent', color: sel ? p.selColor : V.muted, fontFamily: "'Jost',sans-serif", fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s' }}>
                  {p.label}
                </button>
              );
            })}
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontFamily: "'Jost',sans-serif", fontSize: '11px', color: V.muted, whiteSpace: 'nowrap' }}>Sort by:</span>
              <select value={sortOrder} onChange={e => setSortOrder(e.target.value)} style={{ background: V.bg2, border: `0.5px solid rgba(201,168,76,0.3)`, borderRadius: '6px', color: V.muted, fontFamily: "'Jost',sans-serif", fontSize: '12px', padding: '4px 8px', cursor: 'pointer' }}>
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="highest">Highest Premium</option>
                <option value="lowest">Lowest Premium</option>
                <option value="strategy">By Strategy</option>
                <option value="symbol">By Symbol A–Z</option>
              </select>
            </div>
          </div>

          {/* Empty state */}
          {filtered.length === 0 && (
            <div style={{ background: V.bg2, border: `0.5px solid rgba(201,168,76,0.2)`, borderRadius: '10px', padding: '24px', textAlign: 'center' }}>
              <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, fontStyle: 'italic', margin: 0, lineHeight: 1.7 }}>{emptyMsg[tradeFilter] || `No ${filterName[tradeFilter]} found.`}</p>
            </div>
          )}

          {filtered.map(t => {
            const isClosed = t.status !== 'Open';
            const tML = tradeMaxLoss(t);
            const tTheoML = tradeTheoreticalLoss(t);
            const tRisk = accountSize > 0 ? (tML / accountSize) * 100 : 0;
            const tProfit = parseFloat(t.pnl);
            const tTax = isClosed && tProfit > 0 ? tProfit * 0.28 : 0;
            const statusColor = isClosed ? (tProfit > 0 ? V.sage : tProfit < 0 ? V.rose : V.muted) : V.sky;
            return t.id === editId ? (
              /* ── Edit card ── */
              <div key={t.id} style={{ background: V.bg2, border: `1px solid ${V.gold}`, borderRadius: '12px', padding: '20px', width: '100%', boxSizing: 'border-box' }}>
                <div style={{ ...eyebrow, marginBottom: '14px', color: V.gold }}>Editing Trade</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px', marginBottom: '14px' }}>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <div style={fLabel}>Underlying Symbol</div>
                    <input style={inpSm} value={editForm.ticker || ''} onChange={e => setEditForm(f => ({ ...f, ticker: e.target.value.toUpperCase() }))} placeholder="AAPL" />
                  </div>
                  <div>
                    <div style={fLabel}>Strategy<StrategyInfoTooltip /></div>
                    <select style={inpSm} value={editForm.strategy || 'CSP'} onChange={e => setEditForm(f => ({ ...f, strategy: e.target.value }))}>
                      {['CSP', 'CC', 'Wheel', 'Bull Put Spread', 'Bear Call Spread', '0DTE Spread', 'Swing', 'LEAPS'].map(s => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <div style={fLabel}>Entry Date</div>
                    <input style={inpSm} type="date" value={editForm.entryDate || editForm.date || ''} onChange={e => setEditForm(f => ({ ...f, entryDate: e.target.value }))} />
                  </div>
                  <div>
                    <div style={fLabel}>Expiration Date</div>
                    <input style={inpSm} type="date" value={editForm.expiryDate || ''} onChange={e => setEditForm(f => ({ ...f, expiryDate: e.target.value }))} />
                  </div>
                  <div>
                    <div style={fLabel}>Short Strike</div>
                    <input style={inpSm} value={editForm.strike || ''} onChange={e => setEditForm(f => ({ ...f, strike: e.target.value }))} placeholder="e.g. 420" />
                  </div>
                  <div>
                    <div style={fLabel}>Long Strike (optional)</div>
                    <input style={inpSm} value={editForm.strike2 || ''} onChange={e => setEditForm(f => ({ ...f, strike2: e.target.value }))} placeholder="e.g. 415" />
                  </div>
                  <div>
                    <div style={fLabel}>Premium</div>
                    <input style={inpSm} value={editForm.credit || ''} onChange={e => setEditForm(f => ({ ...f, credit: e.target.value }))} placeholder="0.00" />
                  </div>
                  <div>
                    <div style={fLabel}>Contracts</div>
                    <input style={inpSm} type="number" min="1" value={editForm.qty || ''} onChange={e => setEditForm(f => ({ ...f, qty: e.target.value }))} placeholder="1" />
                  </div>
                  <div>
                    <div style={fLabel}>Outcome</div>
                    <select style={inpSm} value={editForm.status || 'Open'} onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}>
                      {['Open', 'Closed - Profit', 'Closed - Loss', 'Closed - Breakeven', 'Assigned', 'Rolled', 'Expired Worthless'].map(s => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <div style={fLabel}>P&L</div>
                    <input style={inpSm} value={editForm.pnl || ''} onChange={e => setEditForm(f => ({ ...f, pnl: e.target.value }))} placeholder="0" />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <div style={fLabel}>Notes</div>
                    <textarea style={{ ...inpSm, minHeight: '60px', resize: 'vertical' }} value={editForm.notes || ''} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} placeholder="Notes..." />
                  </div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  <button style={{ ...btnGold, padding: '10px 20px', fontSize: '13px' }} onClick={saveTrade}>Save Changes →</button>
                  <button style={{ ...btnGhost, padding: '10px 18px', fontSize: '13px' }} onClick={cancelEdit}>Cancel</button>
                </div>
              </div>
            ) : (
              /* ── Display card ── */
              <div key={t.id} style={{ background: V.bg2, border: `0.5px solid ${V.border}`, borderRadius: '12px', padding: '18px 20px' }}>
                {/* Card header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                    <span style={{ ...mono, fontSize: '20px', color: V.goldL, fontWeight: 600 }}>{t.ticker}</span>
                    {t.emotion && <span style={{ fontSize: '16px' }} title="Pre-trade state">{MINI_EMOTIONS.find(e => e.key === t.emotion)?.emoji}</span>}
                    <span style={{ fontSize: '11px', fontFamily: "'Jost',sans-serif", color: V.muted, background: V.bg3, padding: '2px 8px', borderRadius: '4px' }}>{t.strategy}</span>
                    <span style={{ fontSize: '11px', fontFamily: "'Jost',sans-serif", color: statusColor, background: `${statusColor}18`, padding: '2px 8px', borderRadius: '4px', border: `0.5px solid ${statusColor}44` }}>{t.status}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                    <button style={{ ...actBtn(V.gold) }} onClick={() => startEdit(t)}>Edit</button>
                    {deleteConfirm === t.id
                      ? <button style={{ ...actBtn(V.rose), background: 'rgba(196,122,122,0.15)' }} onClick={() => deleteTrade(t.id)}>Confirm?</button>
                      : <button style={{ ...actBtn(V.rose) }} onClick={() => setDeleteConfirm(t.id)}>Delete</button>
                    }
                  </div>
                </div>
                {/* Card detail rows */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '10px 16px', fontSize: '12px', fontFamily: "'Jost', sans-serif" }}>
                  <div>
                    <div style={{ fontSize: '10px', color: V.dim, marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Entry Date</div>
                    <div style={{ color: V.muted }}>{t.entryDate || t.date || '—'}</div>
                  </div>
                  {t.expiryDate && (
                    <div>
                      <div style={{ fontSize: '10px', color: V.dim, marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Expiry</div>
                      <div style={{ color: V.muted }}>{t.expiryDate}</div>
                    </div>
                  )}
                  <div>
                    <div style={{ fontSize: '10px', color: V.dim, marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Strike{t.strike2 ? ' (short)' : ''}</div>
                    <div style={{ ...mono }}>${t.strike}</div>
                  </div>
                  {t.strike2 && (
                    <div>
                      <div style={{ fontSize: '10px', color: V.dim, marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Strike (long)</div>
                      <div style={{ ...mono }}>${t.strike2}</div>
                    </div>
                  )}
                  <div>
                    <div style={{ fontSize: '10px', color: V.dim, marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Premium</div>
                    <div style={{ ...mono, color: V.sage }}>${t.credit}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '10px', color: V.dim, marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Contracts</div>
                    <div style={{ ...mono, color: V.muted }}>{t.qty || 1}</div>
                  </div>
                  {t.pnl && (
                    <div>
                      <div style={{ fontSize: '10px', color: V.dim, marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>P&L</div>
                      <div style={{ ...mono, color: parseFloat(t.pnl) >= 0 ? V.sage : V.rose }}>${t.pnl}</div>
                    </div>
                  )}
                </div>
                {/* Notes */}
                {t.notes && (
                  <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: `0.5px solid rgba(240,237,230,0.06)`, fontSize: '12px', color: V.dim, fontFamily: "'Jost', sans-serif", fontStyle: 'italic', lineHeight: 1.6 }}>{t.notes}</div>
                )}
                {/* Current Mark — open trades only */}
                {!isClosed && (
                  <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: `0.5px solid rgba(240,237,230,0.06)` }}>
                    <div style={{ fontSize: '11px', color: V.muted, fontFamily: "'Jost',sans-serif", marginBottom: '2px' }}>
                      Current Mark <span style={{ color: V.dim, fontStyle: 'italic' }}>(optional)</span>
                    </div>
                    <div style={{ fontSize: '11px', color: V.dim, fontFamily: "'Jost',sans-serif", fontStyle: 'italic', marginBottom: '8px', lineHeight: 1.5 }}>
                      Enter current option price from TOS to see updated risk.
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                      <input
                        type="number"
                        placeholder="e.g. 0.10"
                        step="0.01"
                        min="0"
                        style={{ width: '100px', padding: '6px 10px', background: V.bg3, border: `0.5px solid ${V.border}`, borderRadius: '6px', color: V.text, fontSize: '12px', outline: 'none', fontFamily: "'Jost',sans-serif", boxSizing: 'border-box' }}
                        value={currentMarkInputs[t.id] !== undefined ? currentMarkInputs[t.id] : (t.currentMark != null ? String(t.currentMark) : '')}
                        onChange={e => {
                          setCurrentMarkInputs(m => ({ ...m, [t.id]: e.target.value }));
                          updateCurrentMark(t.id, e.target.value);
                        }}
                      />
                      {(() => {
                        const raw = currentMarkInputs[t.id] !== undefined ? currentMarkInputs[t.id] : (t.currentMark != null ? String(t.currentMark) : '');
                        const mark = parseFloat(raw);
                        if (isNaN(mark) || mark < 0 || raw === '') return null;
                        const currentML = mark * (parseInt(t.qty) || 1) * 100 * 2;
                        const currentRisk = accountSize > 0 ? (currentML / accountSize) * 100 : 0;
                        const rc = riskColor(currentRisk);
                        return (
                          <span style={{ fontSize: '12px', fontFamily: "'Jost',sans-serif", color: rc }}>
                            Current risk: ${currentML.toFixed(0)}{accountSize > 0 ? ` (${currentRisk.toFixed(1)}% of portfolio)` : ''}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                )}
                {/* Risk sub-rows — 3-tier */}
                {accountSize > 0 && tML > 0 && (
                  <div style={{ marginTop: '8px', fontSize: '11px', fontFamily: "'Jost', sans-serif", display: 'flex', flexDirection: 'column', gap: '3px' }}>
                    {isClosed ? (
                      <span style={{ color: V.dim, fontStyle: 'italic' }}>Was {tRisk.toFixed(1)}% of portfolio — {t.status}</span>
                    ) : (() => {
                      const rawMark = currentMarkInputs[t.id] !== undefined ? currentMarkInputs[t.id] : (t.currentMark != null ? String(t.currentMark) : '');
                      const mark = parseFloat(rawMark);
                      const hasCurrentMark = !isNaN(mark) && mark >= 0 && rawMark !== '';
                      const currentML = hasCurrentMark ? mark * (parseInt(t.qty) || 1) * 100 * 2 : null;
                      const currentRiskPct = currentML !== null && accountSize > 0 ? (currentML / accountSize) * 100 : null;
                      return (
                        <>
                          {hasCurrentMark && currentML !== null && (
                            <div style={{ display: 'flex', gap: '6px' }}>
                              <span style={{ color: V.muted }}>Current risk:</span>
                              <span style={{ color: riskColor(currentRiskPct || 0), fontWeight: 500 }}>
                                ${currentML.toFixed(0)}{currentRiskPct !== null ? ` (${currentRiskPct.toFixed(1)}%)` : ''}
                              </span>
                            </div>
                          )}
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <span style={{ color: V.dim }}>Practical risk (2× premium):</span>
                            <span style={{ color: riskColor(tRisk) }}>
                              ${tML.toFixed(0)}{accountSize > 0 ? ` (${tRisk.toFixed(1)}%)` : ''}
                            </span>
                          </div>
                          {tTheoML > tML && isSpreadStrategy(t.strategy) && (
                            <div style={{ display: 'flex', gap: '6px' }}>
                              <span style={{ color: V.dim, fontStyle: 'italic' }}>Theoretical max (spread width):</span>
                              <span style={{ color: V.dim, fontStyle: 'italic' }}>${tTheoML.toFixed(0)}</span>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}
                {tTax > 0 && (
                  <div style={{ marginTop: '4px', fontSize: '11px', color: V.gold, fontFamily: "'Jost', sans-serif" }}>
                    Est. tax to set aside: ${tTax.toFixed(0)} (28% short term)
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    })()}
      {trades.length === 0 && <p style={{ fontSize: '13px', color: V.dim, textAlign: 'center', padding: '20px' }}>No trades logged yet. Add your first paper trade above.</p>}

      {totalTaxEstimate > 0 && (
        <div style={{ background: V.bg2, border: `0.5px solid ${V.border}`, borderRadius: '10px', padding: '18px 20px', marginTop: '14px' }}>
          <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '10px', letterSpacing: '0.15em', textTransform: 'uppercase', color: V.muted, marginBottom: '8px' }}>Tax Estimate — This Year</div>
          <div style={{ ...mono, fontSize: '22px', color: V.gold, marginBottom: '4px' }}>${totalTaxEstimate.toFixed(0)}</div>
          <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.muted, marginBottom: '12px' }}>Total estimated taxes · Based on 28% short-term capital gains rate</div>
          <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '11px', color: V.dim, fontStyle: 'italic', lineHeight: 1.6, margin: 0 }}>
            Tax estimates are for educational reference only. Consult a qualified tax professional regarding your individual circumstances. This is not tax advice.
          </p>
        </div>
      )}

      </React.Fragment>
      )}

      {/* My Patterns Tab */}
      {plTab === 'patterns' && (() => {
        if (closedTrades.length < 10) {
          return (
            <div style={{ padding: '24px 0' }}>
              <div style={{ background: V.bg2, border: `0.5px solid ${V.border}`, borderRadius: '12px', padding: '32px 28px' }}>
                <h3 style={{ ...serif, fontSize: '24px', color: V.gold, fontWeight: 400, margin: '0 0 20px' }}>Your Pattern Analysis</h3>
                <div style={{ height: '6px', background: 'rgba(240,237,230,0.07)', borderRadius: '3px', overflow: 'hidden', maxWidth: '320px', marginBottom: '8px' }}>
                  <div style={{ height: '100%', width: `${(closedTrades.length / 10) * 100}%`, background: `linear-gradient(90deg,${V.gold},${V.goldL})`, borderRadius: '3px' }} />
                </div>
                <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, marginBottom: '20px' }}>{closedTrades.length} of 10 trades logged</div>
                <p style={{ ...serif, fontSize: '15px', color: V.muted, fontStyle: 'italic', lineHeight: 1.9, margin: 0 }}>
                  Your pattern analysis unlocks after 10 trades. Every entry is building a picture of yourself as a trader. Each trade teaches you something — about the market, and about yourself. Keep going.
                </p>
              </div>
            </div>
          );
        }

        const getEmotion = (trade) => {
          if (trade.emotion) return trade.emotion;
          const match = checkins.find(c => c.date === trade.date && c.type === 'pre_trade');
          return match?.emotion || null;
        };
        const byEmotion = {}, byStrategy = {}, byDay = { Sun:[], Mon:[], Tue:[], Wed:[], Thu:[], Fri:[], Sat:[] };
        closedTrades.forEach(t => {
          const won = parseFloat(t.pnl) > 0;
          const em = getEmotion(t);
          if (em) { if (!byEmotion[em]) byEmotion[em] = { wins:0, total:0, pnl:0 }; byEmotion[em].wins += won?1:0; byEmotion[em].total++; byEmotion[em].pnl += parseFloat(t.pnl)||0; }
          const st = t.strategy || 'Other'; if (!byStrategy[st]) byStrategy[st] = { wins:0, total:0 }; byStrategy[st].wins += won?1:0; byStrategy[st].total++;
          const parts = (t.date||'').split('/'); if (parts.length===3) { const d = new Date(parts[2], parseInt(parts[0])-1, parseInt(parts[1])); const dn = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d.getDay()]; if (byDay[dn]) byDay[dn].push(parseFloat(t.pnl)||0); }
        });

        const topEmotions = Object.entries(byEmotion).filter(([,d]) => d.total >= 2).sort((a,b) => b[1].total - a[1].total).slice(0,5);
        const topStrategies = Object.entries(byStrategy).filter(([,d]) => d.total >= 2).sort((a,b) => b[1].total - a[1].total);
        const bestEm  = topEmotions.length ? topEmotions.reduce((b,e) => (e[1].wins/e[1].total) > (b[1].wins/b[1].total) ? e : b, topEmotions[0]) : null;
        const worstEm = topEmotions.length > 1 ? topEmotions.reduce((b,e) => (e[1].wins/e[1].total) < (b[1].wins/b[1].total) ? e : b, topEmotions[0]) : null;
        const bestSt  = topStrategies.length ? topStrategies.reduce((b,s) => (s[1].wins/s[1].total) > (b[1].wins/b[1].total) ? s : b, topStrategies[0]) : null;

        const sC = { background: V.bg2, border: `0.5px solid ${V.border}`, borderRadius: '12px', padding: '20px 22px', marginBottom: '14px' };
        const barRow = (label, wins, total, highlight = false) => {
          const pct = total > 0 ? Math.round((wins/total)*100) : 0;
          const col = pct > 60 ? V.sage : pct >= 40 ? V.gold : V.rose;
          return (
            <div key={label} style={{ marginBottom: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={{ fontSize: '12px', color: highlight ? V.goldL : V.muted, fontFamily: "'Jost',sans-serif" }}>{label}{highlight ? ' ★' : ''}</span>
                <span style={{ ...mono, fontSize: '12px', color: col }}>{pct}% · {total} trade{total!==1?'s':''}</span>
              </div>
              <div style={{ height: '5px', background: 'rgba(240,237,230,0.07)', borderRadius: '3px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${pct}%`, background: col, borderRadius: '3px' }} />
              </div>
            </div>
          );
        };

        return (
          <div style={{ paddingTop: '8px' }}>
            {/* Win rate by emotion */}
            {topEmotions.length > 0 ? (
              <div style={sC}>
                <h3 style={{ ...serif, fontSize: '18px', color: V.gold, fontWeight: 400, margin: '0 0 16px' }}>Trading by Emotional State</h3>
                {topEmotions.map(([key, data]) => { const em = MINI_EMOTIONS.find(e=>e.key===key); return barRow(`${em?.emoji||''} ${em?.label||key}`, data.wins, data.total, bestEm && key===bestEm[0]); })}
              </div>
            ) : (
              <div style={sC}>
                <h3 style={{ ...serif, fontSize: '18px', color: V.gold, fontWeight: 400, margin: '0 0 10px' }}>Trading by Emotional State</h3>
                <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, lineHeight: 1.7, margin: 0 }}>Complete pre-trade check-ins to see how your emotional state affects your trading results.</p>
              </div>
            )}

            {/* Win rate by strategy */}
            {topStrategies.length > 0 && (
              <div style={sC}>
                <h3 style={{ ...serif, fontSize: '18px', color: V.gold, fontWeight: 400, margin: '0 0 16px' }}>Win Rate by Strategy</h3>
                {topStrategies.map(([st, data]) => barRow(st, data.wins, data.total, bestSt && st===bestSt[0]))}
              </div>
            )}

            {/* Best trading days */}
            <div style={sC}>
              <h3 style={{ ...serif, fontSize: '18px', color: V.gold, fontWeight: 400, margin: '0 0 16px' }}>Best Trading Days</h3>
              {['Mon','Tue','Wed','Thu','Fri'].map(day => {
                const dT = byDay[day]; const avg = dT.length ? dT.reduce((s,v)=>s+v,0)/dT.length : 0;
                return (
                  <div key={day} style={{ marginBottom: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <span style={{ fontSize: '12px', color: V.muted, fontFamily: "'Jost',sans-serif" }}>{day}</span>
                      <span style={{ ...mono, fontSize: '12px', color: avg>=0?V.sage:V.rose }}>{dT.length ? `$${avg.toFixed(0)} avg · ${dT.length} trade${dT.length!==1?'s':''}` : '—'}</span>
                    </div>
                    {dT.length > 0 && <div style={{ height: '5px', background: 'rgba(240,237,230,0.07)', borderRadius: '3px', overflow: 'hidden' }}><div style={{ height: '100%', width: `${Math.min(Math.abs(avg)/50*100,100)}%`, background: avg>=0?V.sage:V.rose, borderRadius: '3px' }} /></div>}
                  </div>
                );
              })}
            </div>

            {/* P&L by emotion */}
            {topEmotions.length > 0 && (
              <div style={sC}>
                <h3 style={{ ...serif, fontSize: '18px', color: V.gold, fontWeight: 400, margin: '0 0 16px' }}>P&amp;L by Emotional State</h3>
                {topEmotions.map(([key, data]) => {
                  const em = MINI_EMOTIONS.find(e=>e.key===key); const avg = data.pnl / data.total;
                  return (
                    <div key={key} style={{ marginBottom: '8px', fontFamily: "'Jost',sans-serif", fontSize: '13px' }}>
                      <span style={{ color: V.muted }}>{em?.emoji||''} When trading {em?.label||key}: </span>
                      <span style={{ ...mono, color: avg>=0?V.sage:V.rose }}>${avg.toFixed(0)} avg P&L</span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* The Insight */}
            <div style={{ ...sC, borderLeft: `3px solid ${V.gold}` }}>
              <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '10px', letterSpacing: '0.15em', textTransform: 'uppercase', color: V.muted, marginBottom: '14px' }}>The Pattern Insight</div>
              <p style={{ ...serif, fontSize: '15px', color: V.text, fontStyle: 'italic', lineHeight: 1.9, marginBottom: '18px' }}>
                {[
                  bestEm  && `Your data shows your strongest trading happens when you are ${MINI_EMOTIONS.find(e=>e.key===bestEm[0])?.label||bestEm[0]}.`,
                  worstEm && worstEm[0]!==bestEm?.[0] && `Your most challenging days tend to be when you are ${MINI_EMOTIONS.find(e=>e.key===worstEm[0])?.label||worstEm[0]}.`,
                  bestSt  && `Your best strategy by win rate is ${bestSt[0]}.`,
                ].filter(Boolean).join(' ') || 'Keep logging trades and check-ins to generate your pattern insight.'}
              </p>
              <p style={{ ...serif, fontSize: '14px', color: V.gold, fontStyle: 'italic', margin: '0 0 14px' }}>What do you notice about this?</p>
              <textarea
                value={patternsNote}
                onChange={e => { setPatternsNote(e.target.value); try { localStorage.setItem('tlaw_patterns_note', e.target.value); } catch {} }}
                placeholder="Write your thoughts here..."
                style={{ width: '100%', minHeight: '80px', padding: '10px 12px', background: V.bg3, border: `0.5px solid ${V.border}`, borderRadius: '8px', color: V.text, fontSize: '13px', fontFamily: "'Jost',sans-serif", lineHeight: 1.7, resize: 'vertical', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
          </div>
        );
      })()}
    </div>
  );
}

export default PLTracker
